import * as Yup from "yup";
import i18n from "@/locales/i18n";
import type { SilaeSourceFormData, CounterType } from "@/types/silae";

// Helper function to get translated error messages
const t = (key: string) => i18n.t(`validation.errors.${key}`);

// ===== Helpers =====

/**
 * Vérifie qu'un constructeur Date(j, m, a) a tous ses composants non nuls et non vides.
 * Retourne une clé de traduction si invalide, null si valide.
 */
function validateSilaeDateConstructor(value: string): string | null {
  const content = value.slice(5, -1); // retirer "Date(" et ")"
  const parts = content.split(",").map((p) => p.trim());
  if (parts.length !== 3) return "dsnPeriodicity.dateNotConfigured";
  const [day, month, year] = parts;
  // Un composant est invalide s'il vaut "0", est vide, ou n'est pas configuré
  const isInvalid = (v: string) => !v || v === "0";
  if (isInvalid(day) || isInvalid(month) || isInvalid(year)) {
    return "dsnPeriodicity.dateNotConfigured";
  }
  return null;
}

// ===== Constants =====
const SOURCE_TYPES = [
  "fixed",
  "evp",
  "salary_base",
  "salary_gross",
  "salary_net",
  "stored_variable",
  "cumul_bulletins",
  "cumul_stock_var",
  "advanced_variable",
  "complex_expression",
  "questionnaire_variable",
  "manual_formula",
] as const;

const CUMUL_TYPES = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
] as const;
const STRUCTURE_TYPES = ["simple", "with_conditions", "if_else"] as const;

const SILAE_CONDITION_TYPES = [
  "VAR_SALARY_BASE",
  "VAR_SALARY_GROSS",
  "VAR_SALARY_NET",
  "VAR_CONTRACT_TYPE",
  "VAR_PROFESSIONAL_STATUS",
  "VAR_EMPLOYEE_CATEGORY",
  "VAR_AGE",
  "VAR_SENIORITY",
  "VAR_MONTH",
  "VAR_IS_EXECUTIVE",
  "silae_variable",
  "cumulstockvar",
  "cumul_bulletins",
  "questionnaire_variable",
  "VAR_YEAR_CURRENT",
  "custom",
] as const;

const CUMUL_PERIODS = [
  "year_current",
  "year_previous",
  "quarter_1",
  "quarter_2",
  "quarter_3",
  "quarter_4",
  "month_current",
  "month_previous",
  "custom",
] as const;

const COUNTER_TYPES: readonly CounterType[] = [
  "AjouteCpAcquisRef",
  "AjouteCPAcquisAnt",
  "AjouteCPPrisRef",
  "AjouteCPPrisAnt",
  "AffecteCPSupJoursAcquis",
  "AffecteCPSupJoursPris",
  "AffecteCPSup2JoursAcquis",
  "AffecteCPSup2JoursPris",
  "AjouteRTTAcquis",
  "AjouteRTTpris",
];

const STORAGE_VALUE_TYPES = [
  "bases",
  "tauxs",
  "bases_times_tauxs",
  "custom",
] as const;

// Regex patterns
const RE_QUESTIONNAIRE_INDEX = /^(?:[1-9]|10)$/;
const RE_VARIABLE_NAME = /^[A-Z_][A-Z0-9_]*$/;
const RE_RUBRIQUE = /^[A-Z]\d{2}(\.[A-Z0-9]{1,4})?$/;
const RE_MANUAL_FORMULA = /^[^!"#$%&'_:;<>?@[\\\]^`{|}~]+$/;
const RE_CUMUL_VAR_NAME = /^[A-Za-z0-9_]*$/; // no space, no special characters, no uppercase

// ===== Base Utility Functions =====
const isEmpty = (v: unknown) => v === undefined || v === null || v === "";

const percentageString1to100 = (value?: string) => {
  if (isEmpty(value)) return true;
  const num = parseFloat(String(value));
  return !Number.isNaN(num) && num >= 1 && num <= 100;
};

const createPercentageValidation = (errorKey: string) =>
  Yup.string()
    .optional()
    .test("percentage-range", t(errorKey), percentageString1to100);

// ===== Condition Schema =====
const silaeConditionSchema = Yup.object({
  id: Yup.string().required(),
  type: Yup.string()
    .required(t("condition.typeRequired"))
    .oneOf(SILAE_CONDITION_TYPES, t("condition.typeInvalid")),

  // SILAE Variable
  silaeVariable: Yup.string().when("type", {
    is: (type: string) =>
      type !== "cumulstockvar" &&
      type !== "cumul_bulletins" &&
      type !== "questionnaire_variable" &&
      type !== "custom",
    then: (schema) => schema.required(t("condition.silaeVariableRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  conditionOperator: Yup.string().when("type", {
    is: (type: string) =>
      type !== "cumulstockvar" && type !== "questionnaire_variable",
    then: (schema) =>
      schema.oneOf(
        ["=", "<>", ">", "<", ">=", "<="],
        t("condition.operatorInvalid"),
      ),
    otherwise: (schema) => schema.optional(),
  }),
  conditionValue: Yup.string().when("type", {
    is: (type: string) => type !== "questionnaire_variable",
    then: (schema) => schema.required(t("condition.comparisonValueRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  conditionPercentage: createPercentageValidation("parameter.percentageRange"),

  // CumulStockVar fields
  cumulStockVarName: Yup.string().when("type", {
    is: (type: string) => type === "cumulstockvar",
    then: (schema) =>
      schema
        .required(t("condition.cumulStockVarNameRequired"))
        .matches(RE_CUMUL_VAR_NAME, t("condition.variableNameInvalid")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulStockVarPeriod: Yup.string()
    .when("type", {
      is: (type: string) => type === "cumulstockvar",
      then: (schema) =>
        schema.required(t("condition.cumulStockVarPeriodRequired")),
      otherwise: (schema) => schema.optional(),
    })
    .oneOf(CUMUL_PERIODS as unknown as string[], t("condition.periodInvalid")),

  cumulStockVarDateDebut: Yup.string().when(["type", "cumulStockVarPeriod"], {
    is: (type: string, cumulStockVarPeriod: string) =>
      type === "cumulstockvar" && cumulStockVarPeriod !== "custom",
    then: (schema) =>
      schema.required(t("condition.cumulStockVarDateDebutRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulStockVarDateFin: Yup.string().when(["type", "cumulStockVarPeriod"], {
    is: (type: string, cumulStockVarPeriod: string) =>
      type === "cumulstockvar" && cumulStockVarPeriod !== "custom",
    then: (schema) =>
      schema.required(t("condition.cumulStockVarDateFinRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulStockVarPercentage: createPercentageValidation(
    "cumulStockVar.percentageRange",
  ),
  cumulStockVarEnableAdditional: Yup.boolean().optional(),
  cumulStockVarAdditionalOperator: Yup.string().when(
    ["type", "cumulStockVarEnableAdditional"],
    {
      is: (type: string, cumulStockVarEnableAdditional: boolean) =>
        type === "cumulstockvar" && cumulStockVarEnableAdditional,
      then: (schema) =>
        schema.oneOf(
          ["+", "-", "*", "/"],
          t("cumulStockVar.additionalOperatorInvalid"),
        ),
      otherwise: (schema) => schema.optional(),
    },
  ),
  cumulStockVarAdditionalValue: Yup.string().when(
    ["type", "cumulStockVarEnableAdditional"],
    {
      is: (type: string, cumulStockVarEnableAdditional: boolean) =>
        type === "cumulstockvar" && cumulStockVarEnableAdditional,
      then: (schema) =>
        schema.required(t("cumulStockVar.additionalValueRequired")),
      otherwise: (schema) => schema.optional(),
    },
  ),

  // CumulBulletins fields
  cumulBulletinsDateDebut: Yup.string().when(["type", "cumulBulletinsPeriod"], {
    is: (type: string, cumulBulletinsPeriod: string) =>
      type === "cumul_bulletins" && cumulBulletinsPeriod !== "custom",
    then: (schema) =>
      schema.required(t("condition.cumulBulletinsDateDebutRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulBulletinsDateFin: Yup.string().when(["type", "cumulBulletinsPeriod"], {
    is: (type: string, cumulBulletinsPeriod: string) =>
      type === "cumul_bulletins" && cumulBulletinsPeriod !== "custom",
    then: (schema) =>
      schema.required(t("condition.cumulBulletinsDateFinRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulBulletinsType: Yup.string().when("type", {
    is: (type: string) => type === "cumul_bulletins",
    then: (schema) =>
      schema
        .required(t("condition.cumulBulletinsTypeRequired"))
        .oneOf(
          CUMUL_TYPES as unknown as string[],
          t("cumulBulletins.typeInvalid"),
        ),
    otherwise: (schema) => schema.optional(),
  }),
  cumulBulletinsDivisor: Yup.string().optional(),
  cumulBulletinsPeriod: Yup.string()
    .when("type", {
      is: (type: string) => type === "cumul_bulletins",
      then: (schema) =>
        schema.required(t("condition.cumulBulletinsPeriodRequired")),
      otherwise: (schema) => schema.optional(),
    })
    .oneOf(CUMUL_PERIODS as unknown as string[], t("condition.periodInvalid")),

  // Questionnaire Variable fields
  questionnaireVariableType: Yup.string().when("type", {
    is: (type: string) => type === "questionnaire_variable",
    then: (schema) =>
      schema.required(t("condition.questionnaireVariableTypeRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  questionnaireVariableIndex: Yup.string().when("type", {
    is: (type: string) => type === "questionnaire_variable",
    then: (schema) =>
      schema.required(t("condition.questionnaireVariableIndexRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  questionnaireVariableLabel: Yup.string().optional(),
  questionnaireVariablePercentage: createPercentageValidation(
    "questionnaireVariable.percentageRange",
  ),
  questionnaireVariableValue: Yup.string().when("type", {
    is: (type: string) => type === "questionnaire_variable",
    then: (schema) =>
      schema.required(t("condition.questionnaireVariableValueRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  questionnaireVariableOperator: Yup.string()
    .when("type", {
      is: (type: string) => type === "questionnaire_variable",
      then: (schema) =>
        schema.required(t("condition.questionnaireVariableOperatorRequired")),
      otherwise: (schema) => schema.optional(),
    })
    .oneOf(["=", "<>", ">", "<", ">=", "<="], t("condition.operatorInvalid")),
});

// ===== Source Schema avec profondeur limitée =====
const MAX_DEPTH = 3;

// Définir le type pour éviter les problèmes de récursion
type SilaeSourceSchema = Yup.ObjectSchema<SilaeSourceFormData>;

// Champs de validation communs à tous les niveaux (sans récursion)
const sourceFieldSchemas = {
  hasConditions: Yup.boolean(),
  conditions: Yup.array()
    .of(silaeConditionSchema)
    .when("hasConditions", {
      is: true,
      then: (schema) =>
        schema
          .required(t("condition.required"))
          .min(1, t("condition.minRequired")),
      otherwise: (schema) => schema.optional(),
    }),
  conditionsOperators: Yup.array()
    .of(
      Yup.string().oneOf(["AND", "OR"], t("condition.logicalOperatorInvalid")),
    )
    .optional(),

  sourceType: Yup.string()
    .required(t("sourceType.required"))
    .oneOf(SOURCE_TYPES as unknown as string[], t("sourceType.invalid")),

  // Fixed
  amount: Yup.number().when("sourceType", {
    is: "fixed",
    then: (schema) =>
      schema
        .required(t("baseAmount.required"))
        .min(0, t("baseAmount.negative")),
    otherwise: (schema) => schema.optional(),
  }),

  // EVP
  evpTitle: Yup.string().when("sourceType", {
    is: "evp",
    then: (schema) =>
      schema
        .required(t("evpTitle.required"))
        .matches(RE_CUMUL_VAR_NAME, t("storedVariableName.format"))
        .min(2, t("evpTitle.minLength"))
        .max(50, t("evpTitle.maxLength")),
    otherwise: (schema) => schema.optional(),
  }),
  evpDefaultValue: Yup.string().when("sourceType", {
    is: "evp",
    then: (schema) => schema.required(t("evpDefaultValue.required")),
    otherwise: (schema) => schema.optional(),
  }),

  // Salary
  salaryPercentage: Yup.string().when("sourceType", {
    is: (value: string) =>
      ["salary_base", "salary_gross", "salary_net"].includes(value),
    then: (schema) => schema.required(t("salaryPercentage.required")),
    otherwise: (schema) => schema.optional(),
  }),

  // Stored Variable
  storedVariableName: Yup.string().when("sourceType", {
    is: "stored_variable",
    then: (schema) =>
      schema
        .required(t("storedVariableName.required"))
        .min(2, t("storedVariableName.minLength"))
        .max(50, t("storedVariableName.maxLength"))
        .matches(RE_VARIABLE_NAME, t("storedVariableName.format")),
    otherwise: (schema) => schema.optional(),
  }),

  // Advanced Variable
  advancedVariable: Yup.string().when("sourceType", {
    is: "advanced_variable",
    then: (schema) =>
      schema
        .required(t("advancedVariable.required"))
        .min(2, t("advancedVariable.minLength"))
        .max(50, t("advancedVariable.maxLength")),
    otherwise: (schema) => schema.optional(),
  }),
  advancedVariablePercentage: Yup.string().optional(),
  advancedVariableEnableAdditional: Yup.boolean().optional(),
  advancedVariableAdditionalOperator: Yup.string()
    .optional()
    .oneOf(
      ["+", "-", "*", "/"],
      t("advancedVariableAdditionalOperator.invalid"),
    ),
  advancedVariableAdditionalValue: Yup.string().optional(),
  advancedVariablePostCallVarName: Yup.string()
    .optional()
    .test(
      "post-call-var-name-required",
      t("advancedVariablePostCall.varNameRequired"),
      function (value) {
        const expr = this.parent.advancedVariablePostCallExpression;
        if (expr && expr.trim().length > 0) {
          return !!value && value.trim().length > 0;
        }
        return true;
      },
    ),
  advancedVariablePostCallExpression: Yup.string()
    .optional()
    .test(
      "post-call-expression-required",
      t("advancedVariablePostCall.expressionRequired"),
      function (value) {
        const varName = this.parent.advancedVariablePostCallVarName;
        if (varName && varName.trim().length > 0) {
          return !!value && value.trim().length > 0;
        }
        return true;
      },
    ),
  advancedVariableParameters: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        value: Yup.string().when("isRequired", {
          is: true,
          then: (schema) => schema.required(t("parameter.valueRequired")),
          otherwise: (schema) => schema.optional(),
        }),
        operator: Yup.string().optional(),
        percentage: createPercentageValidation("parameter.percentageRange"),
        includeBulletinCourant: Yup.boolean().optional(),
        shouldIncludeBulletinCourant: Yup.boolean().when(
          "includeBulletinCourant",
          {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional(),
          },
        ),
        isRequired: Yup.boolean().optional(),
        fixedValue: Yup.string().when("isFixed", {
          is: true,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        }),
        options: Yup.array()
          .of(
            Yup.object({
              label: Yup.string().required(),
              code: Yup.string().required(),
            }),
          )
          .optional(),
        note: Yup.string().optional(),
        placeholder: Yup.string().optional(),
        helper: Yup.string().optional(),
        isFixed: Yup.boolean().optional(),
      }),
    )
    .optional(),

  // Questionnaire Variable
  questionnaireVariableType: Yup.string().when("sourceType", {
    is: "questionnaire_variable",
    then: (schema) =>
      schema
        .required(t("questionnaireVariable.typeRequired"))
        .oneOf(
          ["double", "int", "boolean", "date", "text"],
          t("questionnaireVariable.typeInvalid"),
        ),
    otherwise: (schema) => schema.optional(),
  }),
  questionnaireVariableIndex: Yup.string().when("sourceType", {
    is: "questionnaire_variable",
    then: (schema) =>
      schema
        .required(t("questionnaireVariable.indexRequired"))
        .matches(RE_QUESTIONNAIRE_INDEX, t("questionnaireVariable.indexRange")),
    otherwise: (schema) => schema.optional(),
  }),
  questionnaireVariableLabel: Yup.string()
    .optional()
    .max(100, t("questionnaireVariable.labelMaxLength")),
  questionnaireVariablePercentage: createPercentageValidation(
    "questionnaireVariable.percentageRange",
  ),
  questionnaireVariableValue: Yup.string().optional(),
  questionnaireVariableOperator: Yup.string()
    .optional()
    .oneOf(["=", "<>", ">", "<", ">=", "<="], t("condition.operatorInvalid")),

  // Manual Formula
  manualFormula: Yup.string().when("sourceType", {
    is: "manual_formula",
    then: (schema) =>
      schema
        .required(t("manualFormula.required"))
        .min(2, t("manualFormula.minLength"))
        .max(255, t("manualFormula.maxLength"))
        .matches(RE_MANUAL_FORMULA, t("manualFormula.format")),
    otherwise: (schema) => schema.optional(),
  }),

  // Cumul Bulletins
  cumulPeriod: Yup.string().when("sourceType", {
    is: "cumul_bulletins",
    then: (schema) =>
      schema
        .required(t("cumul.periodRequired"))
        .oneOf(CUMUL_PERIODS as unknown as string[], t("cumul.periodInvalid")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulType: Yup.string().when("sourceType", {
    is: "cumul_bulletins",
    then: (schema) =>
      schema
        .required(t("cumul.typeRequired"))
        .oneOf(CUMUL_TYPES as unknown as string[], t("cumul.typeInvalid")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulCustomStartDate: Yup.string().when(["sourceType", "cumulPeriod"], {
    is: (sourceType: string, cumulPeriod: string) =>
      sourceType === "cumul_bulletins" && cumulPeriod === "custom",
    then: (schema) => schema.required(t("cumul.dateStartRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulCustomEndDate: Yup.string().when(["sourceType", "cumulPeriod"], {
    is: (sourceType: string, cumulPeriod: string) =>
      sourceType === "cumul_bulletins" && cumulPeriod === "custom",
    then: (schema) => schema.required(t("cumul.dateEndRequired")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulDivisor: Yup.string()
    .optional()
    .test("cumul-divisor-min", t("cumul.divisorMin"), (value) => {
      if (!value) return true;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 1;
    }),

  // Cumul Stock Var
  cumulStockVarName: Yup.string().when("sourceType", {
    is: "cumul_stock_var",
    then: (schema) =>
      schema
        .required(t("cumulStockVar.nameRequired"))
        .min(2, t("cumulStockVar.nameMinLength"))
        .max(50, t("cumulStockVar.nameMaxLength"))
        .matches(RE_CUMUL_VAR_NAME, t("cumulStockVar.nameFormat")),
    otherwise: (schema) => schema.optional(),
  }),
  cumulStockVarPeriod: Yup.string().when("sourceType", {
    is: "cumul_stock_var",
    then: (schema) =>
      schema
        .required(t("cumulStockVar.periodRequired"))
        .oneOf(
          CUMUL_PERIODS as unknown as string[],
          t("cumulStockVar.periodInvalid"),
        ),
    otherwise: (schema) => schema.optional(),
  }),
  cumulStockVarDateDebut: Yup.string().when(
    ["sourceType", "cumulStockVarPeriod"],
    {
      is: (sourceType: string, period: string) =>
        sourceType === "cumul_stock_var" && period === "custom",
      then: (schema) => schema.required(t("cumulStockVar.dateStartRequired")),
      otherwise: (schema) => schema.optional(),
    },
  ),
  cumulStockVarDateFin: Yup.string().when(
    ["sourceType", "cumulStockVarPeriod"],
    {
      is: (sourceType: string, period: string) =>
        sourceType === "cumul_stock_var" && period === "custom",
      then: (schema) => schema.required(t("cumulStockVar.dateEndRequired")),
      otherwise: (schema) => schema.optional(),
    },
  ),
  cumulStockVarPercentage: Yup.string().optional(),
  cumulStockVarEnableAdditional: Yup.boolean().optional(),
  cumulStockVarAdditionalOperator: Yup.string()
    .optional()
    .oneOf(["+", "-", "*", "/"], t("cumulStockVar.operatorInvalid")),
  cumulStockVarAdditionalValue: Yup.string().optional(),

  // EVP Force
  enableEvpForce: Yup.boolean(),
  evpForceTitle: Yup.string().when("enableEvpForce", {
    is: true,
    then: (schema) =>
      schema
        .required(t("evpForce.titleRequired"))
        .min(2, t("evpForce.titleMinLength"))
        .max(50, t("evpForce.titleMaxLength"))
        .matches(RE_VARIABLE_NAME, "Format: LETTRES_MAJUSCULES_ET_CHIFFRES"),
    otherwise: (schema) => schema.optional(),
  }),
};

const createSilaeSourceSchema = (depth: number = 0): SilaeSourceSchema => {
  // À la profondeur max : schéma complet sans récursion (complexExpression/elseSource non validés)
  if (depth >= MAX_DEPTH) {
    return Yup.object({
      ...sourceFieldSchemas,
      complexExpression: Yup.array().optional(),
      elseSource: Yup.mixed().optional().nullable(),
    }) as SilaeSourceSchema;
  }

  return Yup.object({
    ...sourceFieldSchemas,

    // elseSource : récursif, requis seulement si hasConditions est true.
    //
    // On utilise Yup.lazy avec un bail-out précoce sur null/undefined pour
    // contourner un quirk de Yup : le pattern naïf
    // `Yup.lazy(() => objectSchema.when().otherwise(optional))` évalue le
    // shape de l'objet même quand la valeur est `undefined`, ce qui produit
    // des erreurs `sourceType is required` en cascade sur la chaîne
    // récursive de `elseSource`. Bailer manuellement permet au contrat
    // optional/nullable de réellement fonctionner.
    elseSource: Yup.lazy((value) => {
      if (value === null || value === undefined) {
        return Yup.mixed().optional().nullable() as unknown as SilaeSourceSchema;
      }
      return createSilaeSourceSchema(depth + 1).when("hasConditions", {
        is: true,
        then: (schema) => schema.required(t("elseSource.required")).nullable(),
        otherwise: (schema) => schema.optional().nullable(),
      });
    }),

    // complexExpression : tableau récursif avec variableName obligatoire
    complexExpression: Yup.array()
      .of(
        Yup.lazy(() =>
          createSilaeSourceSchema(depth + 1)
            .omit(["elseSource"])
            .shape({
              variableName: Yup.string()
                .required(t("variableName.required"))
                .min(2, t("variableName.minLength"))
                .max(50, t("variableName.maxLength"))
                .matches(RE_VARIABLE_NAME, t("variableName.format")),
            }),
        ),
      )
      .test(
        "complex-expression-required",
        t("complexExpression.required"),
        function (value) {
          const parent = this.parent;
          if (!parent || parent.sourceType !== "complex_expression") {
            return true;
          }
          return (
            Array.isArray(value) &&
            value.length > 0 &&
            value.every((item) => item && item.variableName)
          );
        },
      ),
  }) as SilaeSourceSchema;
};

// Créer le schéma principal avec profondeur 0
const silaeSourceFormDataSchema: Yup.ObjectSchema<SilaeSourceFormData> =
  createSilaeSourceSchema(0);

// ===== Bonus Schema (SilaeBonusFormData) =====
const silaeBonusFormDataSchema = Yup.object({
  hasConditions: Yup.boolean(),

  conditions: Yup.array()
    .of(silaeConditionSchema)
    .when("hasConditions", {
      is: true,
      then: (schema) =>
        schema
          .required(t("condition.required"))
          .min(1, t("condition.minRequired")),
      otherwise: (schema) => schema.optional(),
    }),

  conditionsOperators: Yup.array()
    .of(
      Yup.string().oneOf(["AND", "OR"], t("condition.logicalOperatorInvalid")),
    )
    .optional(),

  rubriqueNumber: Yup.string()
    .required(t("rubriqueNumber.required"))
    .matches(RE_RUBRIQUE, t("rubriqueNumber.format")),

  calculMethod: Yup.string()
    .required(t("calculMethod.required"))
    .oneOf(["1", "2", "3", "99"], t("calculMethod.invalid")),

  bases: Yup.array()
    .of(silaeSourceFormDataSchema)
    .min(1, "Au moins une source de base est requise")
    .required("Les bases sont requises"),

  tauxs: Yup.array()
    .of(silaeSourceFormDataSchema)
    .when("calculMethod", {
      is: (value: string) => value === "1" || value === "3",
      then: (schema) =>
        schema
          .min(1, "Au moins une source de taux est requise")
          .required("Les taux sont requis pour cette méthode de calcul"),
      otherwise: (schema) => schema.optional(),
    }),

  libelle: Yup.string()
    .required(t("libelle.required"))
    .min(2, t("libelle.minLength"))
    .max(100, t("libelle.maxLength")),

  // Stockage
  enableStockage: Yup.boolean(),
  stockageVariableName: Yup.string().when("enableStockage", {
    is: true,
    then: (schema) =>
      schema
        .required(t("storage.nameRequired"))
        .min(2, t("storage.nameMinLength"))
        .max(50, t("storage.nameMaxLength"))
        .matches(
          /^[a-zA-Z_][a-zA-Z0-9_]*$/,
          "Format: lettres, chiffres et underscores",
        ),
    otherwise: (schema) => schema.optional(),
  }),
  stockageVariableType: Yup.string().when("enableStockage", {
    is: true,
    then: (schema) =>
      schema
        .required("Type de variable requis")
        .oneOf(
          STORAGE_VALUE_TYPES as unknown as string[],
          "Type de variable invalide",
        ),
    otherwise: (schema) => schema.optional(),
  }),
  stockageCustomExpression: Yup.string().when(
    ["enableStockage", "stockageVariableType"],
    {
      is: (enableStockage: boolean, type: string) =>
        enableStockage && type === "custom",
      then: (schema) => schema.required("Expression personnalisée requise"),
      otherwise: (schema) => schema.optional(),
    },
  ),

  // EVP Force
  enableEvpForce: Yup.boolean(),
  evpForceTitle: Yup.string().when("enableEvpForce", {
    is: true,
    then: (schema) =>
      schema
        .required(t("evpForce.titleRequired"))
        .min(2, t("evpForce.titleMinLength"))
        .max(50, t("evpForce.titleMaxLength"))
        .matches(RE_VARIABLE_NAME, "Format: LETTRES_MAJUSCULES_ET_CHIFFRES"),
    otherwise: (schema) => schema.optional(),
  }),

  // Memos
  enableMemos: Yup.boolean(),
  memoLines: Yup.string().when("enableMemos", {
    is: true,
    then: (schema) =>
      schema.required(t("memoLines.required")).min(2, t("memoLines.minLength")),
    otherwise: (schema) => schema.optional(),
  }),

  // Counter Feeding
  enableCounterFeeding: Yup.boolean(),
  counterType: Yup.string().when("enableCounterFeeding", {
    is: true,
    then: (schema) =>
      schema
        .required(t("counterFeeding.typeRequired"))
        .oneOf(
          COUNTER_TYPES as unknown as string[],
          t("counterFeeding.typeInvalid"),
        ),
    otherwise: (schema) => schema.optional(),
  }),
  counterValueType: Yup.string().when("enableCounterFeeding", {
    is: true,
    then: (schema) =>
      schema
        .required("Type de valeur requis")
        .oneOf(STORAGE_VALUE_TYPES, "Type de valeur invalide"),
    otherwise: (schema) => schema.optional(),
  }),
  counterCustomExpression: Yup.string().when(
    ["enableCounterFeeding", "counterValueType"],
    {
      is: (enabled: boolean, valueType: string) =>
        enabled && valueType === "custom",
      then: (schema) => schema.required("Expression personnalisée requise"),
      otherwise: (schema) => schema.optional(),
    },
  ),

  // Périodicité DSN + Bloc 52
  enableDsnPeriodicity: Yup.boolean(),
  dsnDateDebut: Yup.string().test(
    "dsn-date-debut-valid",
    t("dsnPeriodicity.dateDebutRequired"),
    function (value) {
      const enableDsn = this.parent.enableDsnPeriodicity;
      if (!enableDsn) return true;
      if (!value || value.trim() === "")
        return this.createError({
          message: t("dsnPeriodicity.dateDebutRequired"),
        });
      if (value.startsWith("Date(") && value.endsWith(")")) {
        const err = validateSilaeDateConstructor(value);
        if (err) return this.createError({ message: t(err) });
      }
      return true;
    },
  ),
  dsnDateFin: Yup.string().test(
    "dsn-date-fin-valid",
    t("dsnPeriodicity.dateFinRequired"),
    function (value) {
      const enableDsn = this.parent.enableDsnPeriodicity;
      if (!enableDsn) return true;
      if (!value || value.trim() === "")
        return this.createError({
          message: t("dsnPeriodicity.dateFinRequired"),
        });
      if (value.startsWith("Date(") && value.endsWith(")")) {
        const err = validateSilaeDateConstructor(value);
        if (err) return this.createError({ message: t(err) });
      }
      return true;
    },
  ),
  bloc52Code: Yup.string().test(
    "bloc52-required-when-dsn",
    t("dsnPeriodicity.bloc52CodeRequired"),
    function (value) {
      const enableDsn = this.parent.enableDsnPeriodicity;
      if (!enableDsn) return true;
      if (!value)
        return this.createError({
          message: t("dsnPeriodicity.bloc52CodeRequired"),
        });
      const valid = ["026", "027", "028", "029", "030", "custom"];
      if (!valid.includes(value))
        return this.createError({
          message: t("dsnPeriodicity.bloc52CodeInvalid"),
        });
      return true;
    },
  ),
  bloc52CustomCode: Yup.string().test(
    "bloc52-custom-valid",
    t("dsnPeriodicity.bloc52CustomCodeRequired"),
    function (value) {
      const enableDsn = this.parent.enableDsnPeriodicity;
      const code = this.parent.bloc52Code;
      if (!enableDsn || code !== "custom") return true;
      if (!value)
        return this.createError({
          message: t("dsnPeriodicity.bloc52CustomCodeRequired"),
        });
      if (!/^\d{3}$/.test(value))
        return this.createError({
          message: t("dsnPeriodicity.bloc52CustomCodeFormat"),
        });
      return true;
    },
  ),
});

// ===== Branch Schema (SilaeBranchFormData) =====
const silaeBranchFormDataSchema = Yup.object({
  bonuses: Yup.array()
    .of(silaeBonusFormDataSchema)
    .min(1, "Au moins une prime est requise")
    .required("Les primes sont requises"),
});

// ===== Main Schema (SilaeFormData) =====
export const silaeValidationSchema = Yup.object({
  structureType: Yup.string()
    .required("Le type de structure est requis")
    .oneOf(
      STRUCTURE_TYPES as unknown as string[],
      "Type de structure invalide",
    ),

  // Exclusions
  hasExclusions: Yup.boolean(),
  exclusions: Yup.array()
    .of(silaeConditionSchema)
    .when("hasExclusions", {
      is: true,
      then: (schema) =>
        schema
          .required(t("condition.required"))
          .min(1, t("condition.minRequired")),
      otherwise: (schema) => schema.optional(),
    }),
  exclusionsOperators: Yup.array()
    .of(
      Yup.string().oneOf(["AND", "OR"], t("condition.logicalOperatorInvalid")),
    )
    .optional(),
  // Conditions principales
  conditions: Yup.array()
    .of(silaeConditionSchema)
    .when("structureType", {
      is: (structureType: string) =>
        structureType === "with_conditions" || structureType === "if_else",
      then: (schema) =>
        schema
          .required(t("condition.required"))
          .min(1, t("condition.minRequired")),
      otherwise: (schema) => schema.optional(),
    }),
  conditionsOperators: Yup.array()
    .of(
      Yup.string().oneOf(["AND", "OR"], t("condition.logicalOperatorInvalid")),
    )
    .optional(),

  // Branches IF et ELSE
  ifBranch: silaeBranchFormDataSchema.required("La branche IF est requise"),

  elseBranch: Yup.mixed().when("structureType", {
    is: "if_else",
    then: () =>
      silaeBranchFormDataSchema.required("La branche ELSE est requise"),
    otherwise: (schema) => schema.optional(),
  }),

  comment: Yup.string().optional(),
});

export default silaeValidationSchema;
